/**
 * Mirrors the backend's `MAX_PRIOR_CONTEXT_CHARS` (8000) in
 * packages/backend/src/lib/foundry: POST /api/mcp-codebase/ask rejects a
 * longer `priorContext`, so the builder below never produces one.
 */
export const MAX_PRIOR_CONTEXT_CHARS = 8000;

export interface PriorTurn {
  question: string;
  answer: string;
}

const QUESTION_LABEL = 'Q: ';
const ANSWER_LABEL = '\nA: ';
const TURN_SEPARATOR = '\n\n';
const TRIM_MARKER = ' [trimmed]';

/** The text cut to at most `max` characters, marker included; undefined if nothing useful fits. */
function fit(text: string, max: number): string | undefined {
  if (text.length <= max) return text;
  if (max <= TRIM_MARKER.length) return undefined;
  return text.slice(0, max - TRIM_MARKER.length) + TRIM_MARKER;
}

/** One turn within `budget`, trimming the answer first; undefined if the question cannot fit whole. */
function serializeWholeQuestion(
  { question, answer }: PriorTurn,
  budget: number,
): string | undefined {
  const asked = QUESTION_LABEL + question;
  if (asked.length > budget) return undefined;
  const fittedAnswer = fit(answer, budget - asked.length - ANSWER_LABEL.length);
  return fittedAnswer === undefined
    ? asked
    : asked + ANSWER_LABEL + fittedAnswer;
}

/**
 * Builds a follow-up's `priorContext` from the conversation so far (oldest
 * first in `turns`), newest turn first in the result. The whole serialized
 * string, labels, separators and trim markers included, stays within `cap`:
 *
 * - The newest turn's answer is trimmed first, then its question.
 * - Older turns are added while their whole question fits, with their answer
 *   trimmed to what remains; the first that does not fit is omitted along
 *   with every turn before it.
 * - Undefined when no turn fits, so the request omits `priorContext`.
 */
export function buildPriorContext(
  turns: PriorTurn[],
  cap: number = MAX_PRIOR_CONTEXT_CHARS,
): string | undefined {
  const newestFirst = [...turns].reverse();
  const [newest, ...older] = newestFirst;
  if (!newest) return undefined;

  const first =
    serializeWholeQuestion(newest, cap) ??
    (() => {
      const question = fit(newest.question, cap - QUESTION_LABEL.length);
      return question === undefined ? undefined : QUESTION_LABEL + question;
    })();
  if (first === undefined) return undefined;

  let context = first;
  for (const turn of older) {
    const serialized = serializeWholeQuestion(
      turn,
      cap - context.length - TURN_SEPARATOR.length,
    );
    if (serialized === undefined) break;
    context += TURN_SEPARATOR + serialized;
  }
  return context;
}
